/** PROTOTYPE — shared helpers for HRP reconciliation UI variants. */

export const MATCH_LABEL = {
    exact: "Exact",
    fuzzy: "Fuzzy",
    "extracted-only": "New (extracted)",
    "hrp-only": "HRP only",
    "renewal-only": "Renewal document",
    "exact+renewal": "Exact + renewal document",
    "fuzzy+renewal": "Fuzzy + renewal document",
    "extracted-only+renewal": "Extracted + renewal document",
    "hrp-only+renewal": "HRP + renewal document",
};

export const ACTION_LABEL = {
    amend: "Update",
    create: "Create",
    skip: "Ignore",
    keep: "Keep HRP",
};

const formatAmount = (amount, feeType) => {
    if (amount == null || amount === "") return null;
    const num = feeType === "FLAT"
        ? `$${Number(amount).toLocaleString()}`
        : `$${amount}`;
    return feeType && feeType !== "FLAT" ? `${num} ${feeType}` : num;
};

export const AmountCell = ({ amount, feeType, muted }) => {
    const formatted = formatAmount(amount, feeType);
    if (!formatted) return <span className="billing-rate-builder__muted">—</span>;
    return <div className={muted ? "billing-rate-builder__muted" : undefined}>{formatted}</div>;
};

export const MatchBadge = ({ matchType }) => {
    const base = matchType?.includes("renewal-only")
        ? "unmatched"
        : matchType?.split("+")[0] ?? matchType;
    return (
        <span className={`billing-rate-builder__confidence-badge billing-rate-builder__confidence-badge--${base}`}>
            {MATCH_LABEL[matchType] ?? matchType}
        </span>
    );
};

export const stripHrpPrefix = (hrpName) =>
    String(hrpName ?? "").replace(/^WHEEL\s+/i, "").trim();

export const getRowStatus = (row) => {
    if (!row.confirmed && !row.skipped) return "Pending";
    if (row.skipped || row.userAction === "skip") return "Ignored";
    if (row.userAction === "keep") return "Kept";
    if (row.userAction === "create") return "Created";
    if (row.userAction === "amend") return "Updated";
    return row.skipped ? "Ignored" : "Updated";
};

export const getNewAmount = (row) =>
    (row.renewalFeeIndex != null ? row.renewalNewAmount : (row.renewalNewAmount ?? row.extractedAmount));

export const getExtractedLabel = (row) => row.extractedFeeDescription || row.extractedName;

export const rowNeedsReview = (row) =>
    !row.confirmed
    && !row.skipped
    && (
        row.matchType?.includes("fuzzy")
        || row.matchType === "hrp-only+renewal"
    );

export const rowIsReady = (row) =>
    !row.confirmed
    && !row.skipped
    && !rowNeedsReview(row);

export const applyKeepToRow = (row) => {
    if (!row.hrpComponentName) {
        return { ...row, confirmed: true, skipped: false, userAction: "keep" };
    }
    return {
        ...row,
        confirmed: true,
        skipped: false,
        userAction: "keep",
        extractedFeeDescription: stripHrpPrefix(row.hrpComponentName),
        extractedName: row.hrpComponentName,
        extractedAmount: row.hrpCurrentAmount ?? row.extractedAmount,
        renewalNewAmount: row.hrpCurrentAmount ?? row.renewalNewAmount,
        nameMismatch: false,
        hrpKeepApplied: true,
    };
};

export const applyUpdateToRow = (row) => ({
    ...row,
    confirmed: true,
    skipped: false,
    userAction: "amend",
});

export const applyIgnoreToRow = (row) => ({
    ...row,
    confirmed: true,
    skipped: true,
    userAction: "skip",
    submitExcluded: true,
});

export const applyCreateToRow = (row) => ({
    ...row,
    confirmed: true,
    skipped: false,
    userAction: "create",
});

export const showKeepButton = (row) =>
    Boolean(row.hrpComponentName)
    && row.matchType !== "renewal-only"
    && row.matchType !== "extracted-only";

export const showUpdateButton = (row) =>
    row.matchType === "hrp-only+renewal"
    || (
        row.matchType !== "hrp-only"
        && row.matchType !== "renewal-only"
        && row.matchType !== "extracted-only"
    );

export const showCreateButton = (row) => row.matchType === "renewal-only";

export const showIgnoreButton = () => true;

export const PrototypeStatePanel = ({ rows }) => {
    const summary = {
        pending: rows.filter((r) => !r.confirmed && !r.skipped).length,
        updated: rows.filter((r) => r.userAction === "amend").length,
        kept: rows.filter((r) => r.userAction === "keep").length,
        created: rows.filter((r) => r.userAction === "create").length,
        ignored: rows.filter((r) => r.userAction === "skip").length,
    };

    return (
        <div className="billing-rate-builder__prototype-state">
            <strong>Prototype state</strong>
            {" — "}
            {summary.pending} pending · {summary.updated} updated · {summary.kept} kept ·{" "}
            {summary.created} created · {summary.ignored} ignored
            <details className="billing-rate-builder__prototype-state-details">
                <summary>Full row JSON</summary>
                <pre>{JSON.stringify(rows, null, 2)}</pre>
            </details>
        </div>
    );
};
